import { ModuleWrapper } from '@/components'
import { useQuery } from '@apollo/client'
import { Modal, usePrompt } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { useState } from 'react'
import { isMobileOnly } from 'react-device-detect'
import { Link } from 'react-router'
import styled, { keyframes } from 'styled-components'
import { Button, Card, Input } from '../../../../components_deprecated'
import { useAdmin, useCompany, useOnErrorAuto } from '../../../../utils'
import { ExportFormat, ExportOption } from '../../../exports'
import type {
  AllFilledOutFormsQuery,
  AllFilledOutFormsQueryVariables,
  AllFormTemplatesQuery,
} from '../../../formcreator'
import { FormForm } from '../../../formcreator/components'
import {
  ALL_FILLED_OUT_FORMS_QUERY,
  ALL_FORM_TEMPLATES_QUERY,
} from '../../../formcreator/queries'
import type { Filter } from '../../types'
import { useMaterialExportMutations } from '../mutations.hooks'
import { ExportEquipmentModal } from './ExportEquipmentModal'
import GenericFilter from './GenericFilter'
import MaterialTable from './MaterialTable'

const spin = keyframes`
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
`

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`

const Spinner = styled.div`
  border: 4px solid ${props => props.theme.colors.secondary};
  border-left-color: ${props => props.theme.colors.secondary};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spin} 1s linear infinite;
`

const Controls = styled(Card)`
  display: grid;
  grid-template-columns: 1fr;
  grid-auto-flow: column;
  gap: 1rem;

  margin-bottom: 1rem;
  padding: 1rem;

  @media (max-width: 768px) {
    grid-auto-flow: row;
  }
`

const ExportButton = styled(Button)`
  margin-left: auto;
  padding: 0 2rem;
  font-weight: 500;

  @media (max-width: 768px) {
    margin-left: 0;
    order: -1;
    width: 100%;
  }
`

const LinkStyled = styled(Link)`
  margin-left: 4px;
  color: ${props => props.theme.colors.secondary};
`

function MaterialsTab() {
  const translations = useTranslate({
    materials: 'common.materials',
    addMaterial: 'common.add-material',
    noData: 'common.no-data',
    noForm: 'common.no-form',
    export: 'common.export',
    here: 'common.here',
  })

  const isAdmin = useAdmin()
  const addPrompt = usePrompt()
  const company = useCompany()
  const [open, setOpen] = useState<boolean>(false)
  const onErrorAuto = useOnErrorAuto()
  const { exportMaterials } = useMaterialExportMutations()
  const {
    formValues: filter,
    formChangeHandler: handler,
    updateForm,
  } = useForm<Filter>({
    values: {
      q: undefined,
      excludeTypes: [],
      orderBy: '',
    },
  })

  const { data, loading } = useQuery<
    AllFormTemplatesQuery,
    { q: string; archived: boolean; orderBy: string; type: string }
  >(ALL_FORM_TEMPLATES_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      q: '',
      archived: false,
      orderBy: 'name',
      type: 'material',
    },
    onCompleted(data) {
      if (!data) return
    },
    onError: onErrorAuto(),
  })

  const formData = data?.allFormTemplates.edges[0]?.node
  const templateId = formData?.id

  const {
    data: table,
    loading: loadingTable,
    refetch,
  } = useQuery<AllFilledOutFormsQuery, AllFilledOutFormsQueryVariables>(
    ALL_FILLED_OUT_FORMS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      skip: !templateId,
      variables: {
        formTemplate: templateId,
        dynamicOrderingBy: filter?.orderBy,
        q: filter?.q,
      },
      onError: onErrorAuto(),
    }
  )

  function handleSubmit(value: boolean) {
    if (!value) return
    void refetch()
    setOpen(false)
  }

  const getUniqueTypes = (edges: any[] | undefined): string[] => {
    if (!edges) return []

    const values = edges.flatMap(
      e => e.node?.values?.map((v: { value: string }) => v.value) ?? []
    )

    return Array.from(new Set(values.filter(Boolean))) as string[]
  }

  const filters = getUniqueTypes(table?.allFilledOutForms?.edges)

  const filteredEdges =
    table?.allFilledOutForms.edges?.filter(edge => {
      if (!edge?.node) return false

      if (filter?.excludeTypes?.length) {
        const values = edge.node.values?.map(v => v?.value)
        if (values.some(v => filter?.excludeTypes?.includes(v))) {
          return false
        }
      }

      return true
    }) ?? []

  async function doExport(values: { exportFormat: ExportFormat }) {
    await exportMaterials({
      variables: {
        exportType: 'vehicles',
        formTemplateId: templateId ?? '',
        company: company.id,
        exportFormat: values.exportFormat,
        exportOption:
          values.exportFormat === ExportFormat.PDF
            ? ExportOption.DOWNLOAD
            : ExportOption.SAVE,
      },
    })
  }

  async function handleExport() {
    const { data } = await addPrompt<{ exportFormat: ExportFormat } | null>(
      resolve => <ExportEquipmentModal onSubmit={resolve} />
    )
    if (!data) return

    void doExport(data)
  }

  return (
    <ModuleWrapper
      className="p-0 md:px-0 md:pt-0 md:pb-0"
      title={translations.materials}
      headerContent={
        isAdmin && (
          <ExportButton
            iconLeftProps={{ icon: 'download' }}
            fullWidth={isMobileOnly}
            disabled={!formData || loading}
            onClick={handleExport}
          >
            <span>{translations.export}</span>
          </ExportButton>
        )
      }
    >
      <Controls>
        <Input
          value={filter.q ?? ''}
          type="search"
          fullWidth
          onChange={handler('q')}
        />

        <GenericFilter
          fields={[
            {
              key: 'types',
              label: 'Types',
              type: 'multiSelect',
              values: filters,
              value: filter?.excludeTypes
                ? filters.filter(v => !filter?.excludeTypes?.includes(v))
                : filters,
              onChange: val => {
                const excluded = filters.filter(
                  v => !(val as string[]).includes(v)
                )
                updateForm({ excludeTypes: excluded })
              },
            },
          ]}
        />

        <Button
          iconLeftProps={{ icon: 'plus', size: '1rem', type: 'solid' }}
          color="primary"
          onClick={() => setOpen(true)}
          disabled={!formData || loading || loadingTable}
        >
          {translations.addMaterial}
        </Button>
      </Controls>
      {!formData && (
        <span>
          {translations.noForm}
          <LinkStyled to="/settings/vehicles">{translations.here}</LinkStyled>
        </span>
      )}

      {!loading && !loadingTable ? (
        formData &&
        table && (
          <MaterialTable
            formFields={formData?.formFields}
            edges={filteredEdges ?? []}
            filter={filter}
            onSort={handler('orderBy')}
          />
        )
      ) : (
        <LoaderWrapper>
          <Spinner />
        </LoaderWrapper>
      )}
      {formData && !loading && !loadingTable && (
        <Modal
          open={open}
          offset={isMobileOnly ? 8 : 36}
          placement="top-center"
          direction="from-top"
          onClose={() => setOpen(false)}
        >
          <FormForm
            className="form"
            form={formData}
            onAfterSubmit={handleSubmit}
          />
        </Modal>
      )}
    </ModuleWrapper>
  )
}

export default MaterialsTab
